import { Link } from 'react-router-dom';
import { useBlog } from '@/lib/blog/context';
import { format } from 'date-fns';

export function BlogList() {
  const { articles, loading, error, currentPage, totalPages, setPage } = useBlog();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-red-600 mb-2">Error Loading Blog Posts</h2>
        <p className="text-gray-600">{error.message}</p>
      </div>
    );
  }

  if (!articles.length) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Posts Found</h2>
        <p className="text-gray-600">Check back soon for new content!</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => (
          <div key={article.id} className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
            <Link to={`/blog/${article.slug}`} className="block">
              {article.image && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.headline}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-purple-600">
                  {article.headline}
                </h2>
                <p className="text-gray-600 text-sm line-clamp-3">{article.metaDescription}</p>
              </div>
            </Link>
            
            <div className="px-6 pb-6">
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                {article.category && (
                  <Link
                    to={`/blog/category/${article.category.slug}`}
                    className="text-purple-600 hover:text-purple-700"
                  >
                    {article.category.title}
                  </Link>
                )}
                <span>{format(new Date(article.createdAt), 'MMM d, yyyy')}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag.slug}
                    to={`/blog/tag/${tag.slug}`}
                    className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded hover:bg-purple-100 hover:text-purple-600"
                  >
                    {tag.title}
                  </Link>
                ))}
              </div>

              {article.readingTime && (
                <div className="mt-4 text-sm text-gray-500">
                  {article.readingTime} min read
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 py-8">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 0}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  currentPage === i
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
} 